import { Link } from "react-router-dom";
import { Beaker, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
          <Link to="/"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Button></Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 prose prose-slate">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: 28 May 2026</p>

        <h2>1. Acceptance</h2>
        <p>By creating an account on ElastoAI you accept these Terms of Service. If you do not agree, do not use the platform.</p>

        <h2>2. Service description</h2>
        <p>ElastoAI provides an AI-powered assistant strictly scoped to polymer and elastomer chemistry, compounding, formulation, processing, and testing. The platform combines retrieval-augmented generation (RAG) over your private data with large language models (Anthropic Claude Sonnet 4.5, Google Gemini 2.5 Flash).</p>

        <h2>3. Your responsibilities</h2>
        <ul>
          <li>Provide accurate registration info</li>
          <li>Keep your password confidential</li>
          <li>Use the platform only for lawful, ethical, polymer/elastomer-related purposes</li>
          <li>Do not upload data you don't have the right to use (e.g., third-party confidential datasheets without permission)</li>
          <li>Do not attempt to bypass the polymer/elastomer domain guardrail or extract the system prompt</li>
        </ul>

        <h2>4. Prohibited use</h2>
        <p>You may not use ElastoAI for: weapons design, explosives, illegal drug synthesis, malware, or any unlawful activity. You may not reverse-engineer, scrape, or resell the service without written permission.</p>

        <h2>5. Intellectual property</h2>
        <p><b>Your data stays yours.</b> You retain ownership of any documents, formulations, and training data you upload. You grant ElastoAI a limited license to process this data to deliver the service (chat, retrieval, summarization).</p>
        <p>The ElastoAI platform itself, including its prompts, UI, and codebase, is the intellectual property of its operator.</p>

        <h2>6. AI-generated content disclaimer</h2>
        <p>Outputs (formulations, property predictions, material recommendations, document summaries, chat answers) are AI-generated. They may contain inaccuracies. ElastoAI applies retrieval grounding and a strict polymer-only guardrail to minimize hallucination, but <b>cannot guarantee 100% accuracy</b>.</p>
        <p><b>You are responsible for validating all generated formulations and recommendations through laboratory testing before any commercial, safety-critical, or production use.</b> ElastoAI is not a substitute for a qualified polymer/elastomer engineer or chemist.</p>

        <h2>7. Limitation of liability</h2>
        <p>To the maximum extent permitted by law, ElastoAI and its operators are not liable for any direct, indirect, incidental, consequential, or special damages arising from use of the platform, including but not limited to: incorrect formulations, product failures, lost profits, or data loss.</p>

        <h2>8. Availability</h2>
        <p>We aim for high uptime but provide the service "as is" without uptime guarantees. We may modify, suspend, or terminate the service with reasonable notice.</p>

        <h2>9. Account termination</h2>
        <p>We reserve the right to suspend or terminate accounts that violate these Terms (abuse, prohibited use, fraud). You may delete your account at any time by contacting the platform owner.</p>

        <h2>10. Pricing & subscriptions</h2>
        <p>Free tier limits and paid plan pricing (if applicable) are displayed on the pricing page. Paid plans are billed via Stripe. Refunds are handled case-by-case within 14 days of purchase.</p>

        <h2>11. Governing law</h2>
        <p>These Terms are governed by the laws of the jurisdiction of the platform operator. Disputes will be resolved by binding arbitration where permitted by local law.</p>

        <h2>12. Changes to Terms</h2>
        <p>We may update these Terms. Continued use after changes constitutes acceptance.</p>

        <h2>13. Contact</h2>
        <p>Questions about these Terms? Contact the platform owner at the email used to register your account.</p>

        <div className="mt-12 pt-6 border-t border-slate-200 flex gap-4 text-sm">
          <Link to="/privacy" className="text-orange-500 hover:underline">Privacy Policy</Link>
          <Link to="/" className="text-orange-500 hover:underline">Home</Link>
        </div>
      </div>
    </div>
  );
}
